import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export const useSupabaseAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  // Initialize from sessionStorage first (set by main.tsx IIFE before Supabase processes hash)
  // Fallback to hash check in case IIFE didn't run or hash is still present
  const [isSignupVerification, setIsSignupVerification] = useState(() => {
    if (sessionStorage.getItem('isSignupVerification') === 'true') {
      console.log('useSupabaseAuth: Found isSignupVerification flag in sessionStorage');
      return true;
    }
    // Fallback: check hash directly (in case it hasn't been consumed yet)
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      if (params.get('type') === 'signup') {
        console.log('useSupabaseAuth: Detected signup verification from hash');
        return true;
      }
    }
    return false;
  });

  useEffect(() => {
    // Check for signup hash BEFORE Supabase processes it
    const hash = window.location.hash;
    if (hash) {
      const params = new URLSearchParams(hash.substring(1));
      if (params.get('type') === 'signup') {
        console.log('useSupabaseAuth: Detected signup verification BEFORE Supabase processes it');
        setIsSignupVerification(true);
        // Clear stale localStorage immediately
        localStorage.removeItem('accountType');
        localStorage.removeItem('currentUser');
      }
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session ? 'session exists' : 'no session');
        setSession(session);
        setUser(session?.user ?? null);
        
        // Detect PASSWORD_RECOVERY event
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery detected, setting recovery mode');
          setIsRecoveryMode(true);
        }
        
        // Detect SIGNED_IN event from signup verification
        if (event === 'SIGNED_IN' && session?.user) {
          const hash = window.location.hash;
          const params = new URLSearchParams(hash.substring(1));
          const isFromSignupVerification = params.get('type') === 'signup' || 
            sessionStorage.getItem('isSignupVerification') === 'true' ||
            sessionStorage.getItem('pendingSignupVerification') === 'true';
          
          // Also check if user was created recently (within last 60 seconds) - indicates fresh signup
          const createdAt = new Date(session.user.created_at).getTime();
          const now = Date.now();
          const isRecentSignup = (now - createdAt) < 60000; // 60 seconds
          
          if (isFromSignupVerification || isRecentSignup) {
            console.log('useSupabaseAuth: Signup verification detected on SIGNED_IN', {
              hashType: params.get('type'),
              isRecentSignup,
              pendingFlag: sessionStorage.getItem('pendingSignupVerification')
            });
            sessionStorage.removeItem('pendingSignupVerification');
            sessionStorage.setItem('isSignupVerification', 'true');
            localStorage.removeItem('accountType');
            localStorage.removeItem('currentUser');
            setIsSignupVerification(true);
          }
        }
      }
    );

    // THEN check for existing session - only set loading false after this completes
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session ? 'session exists' : 'no session');
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  return { user, session, loading, isRecoveryMode, isSignupVerification };
};